import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Search,
  Filter,
  Star,
  Clock,
  DollarSign,
  ArrowLeft,
  Store,
  Grid,
  List,
  MessageSquare,
  CheckCircle,
  ShoppingBag,
  Briefcase,
  Tag,
  ChevronRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import OrderModal from '../../components/community/OrderModal';
import { PRODUCT_SERVICE_CATEGORIES } from '../../config/categories';

const ServiceListPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // Order modal state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  
  const { selectedBusiness, getBusinessDetails } = useMarketplaceStore();

  useEffect(() => {
    loadData();
  }, [businessId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await getBusinessDetails(businessId);
      await loadServices();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load services');
    } finally {
      setIsLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const response = await marketplaceAPI.getServices(businessId, { status: 'ACTIVE' });
      setServices(response.data.services || []);
    } catch (error) {
      console.error('Failed to load services:', error);
    }
  };

  // Get unique categories from services
  const serviceCategories = ['all', ...new Set(services.map(s => s.category).filter(Boolean))];

  const getCategoryIcon = (categoryId) => {
    const category = PRODUCT_SERVICE_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || '🛠️';
  };

  const getCategoryName = (categoryId) => {
    const category = PRODUCT_SERVICE_CATEGORIES.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getSortedServices = () => {
    let sorted = [...services];
    
    switch (sortBy) {
      case 'price_low':
        return sorted.sort((a, b) => a.basePrice - b.basePrice);
      case 'price_high':
        return sorted.sort((a, b) => b.basePrice - a.basePrice);
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'popular':
        return sorted.sort((a, b) => (b.completedOrders || 0) - (a.completedOrders || 0));
      case 'featured':
      default:
        return sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-2.5 w-2.5 sm:h-4 sm:w-4 ${
              star <= Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleInquire = (service) => {
    navigate(`/community/marketplace/business/${businessId}`, {
      state: { openMessageModal: true, service }
    });
  };

  const handleOrder = (service) => {
    if (!user) {
      toast.error('Please login to place an order');
      return;
    }
    setSelectedService(service);
    setShowOrderModal(true);
  };

  // Render category filter UI
  const renderCategoryFilter = () => {
    if (serviceCategories.length <= 1) return null;

    return (
      <div className="flex overflow-x-auto hide-scrollbar space-x-2 pb-2">
        {serviceCategories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === cat
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30'
                : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
            }`}
          >
            {cat === 'all' ? (
              'All Services'
            ) : (
              <span className="flex items-center gap-1">
                <span>{getCategoryIcon(cat)}</span>
                {getCategoryName(cat)}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  const filteredServices = getSortedServices().filter(service => {
    const matchesSearch = service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || service.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Mobile Filters Modal
  const MobileFiltersModal = () => (
    <div className="fixed inset-0 z-50 md:hidden">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileFilters(false)} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-neomorph max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-primary-100/50 px-4 py-3 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Filters & Sort</h3>
          <button onClick={() => setShowMobileFilters(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Sort Options */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sort By</h4>
            <div className="space-y-1">
              {[
                { value: 'featured', label: 'Featured' },
                { value: 'newest', label: 'Newest' },
                { value: 'popular', label: 'Most Popular' },
                { value: 'price_low', label: 'Price: Low to High' },
                { value: 'price_high', label: 'Price: High to Low' },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSortBy(option.value);
                    setShowMobileFilters(false);
                  }}
                  className={`w-full flex items-center px-3 py-2.5 rounded-lg text-sm ${
                    sortBy === option.value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                  }`}
                >
                  {option.label}
                  {sortBy === option.value && <CheckCircle className="h-4 w-4 ml-auto text-primary-600" />}
                </button>
              ))}
            </div>
          </div>

          {/* Category Filters */}
          {serviceCategories.length > 1 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
              <div className="grid grid-cols-2 gap-2">
                {serviceCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category);
                      setShowMobileFilters(false);
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${
                      selectedCategory === category
                        ? 'bg-primary-50 text-primary-700 border border-primary-200'
                        : 'bg-gray-50 text-gray-600 border border-transparent'
                    }`}
                  >
                    {category === 'all' ? 'All Services' : (
                      <span className="flex items-center gap-1">
                        <span>{getCategoryIcon(category)}</span>
                        {getCategoryName(category)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={() => {
              setShowMobileFilters(false);
            }}
            className="w-full py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-lg mt-2"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#E8ECF5] pb-16 sm:pb-0">
      {/* Header */}
      <div className="bg-white border-b border-primary-100/30 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="mr-2 sm:mr-3 md:mr-4 p-1.5 sm:p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1 mb-0.5 sm:mb-1">
                  <div className="p-1 sm:p-1.5 bg-primary-50 rounded-lg flex-shrink-0">
                    <Store className="h-3 w-3 sm:h-4 sm:w-4 text-primary-600" />
                  </div>
                  <button
                    onClick={() => navigate(`/community/marketplace/business/${businessId}`)}
                    className="text-[10px] sm:text-sm text-gray-600 hover:text-gray-900 truncate max-w-[120px] sm:max-w-none"
                  >
                    {selectedBusiness?.name}
                  </button>
                  <ChevronRight className="h-2.5 w-2.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                  <h1 className="text-sm sm:text-base md:text-xl font-bold text-gray-800 truncate">Services</h1>
                </div>
                <p className="text-[9px] sm:text-[11px] md:text-sm text-gray-500">
                  {filteredServices.length} of {services.length} services available
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-4 sm:py-6 md:py-8 px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 mb-4 sm:mb-6">
          <div className="flex flex-col space-y-3 sm:space-y-4">
            {/* Search Row */}
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search services..."
                  className="w-full pl-8 sm:pl-10 pr-2.5 sm:pr-4 py-1.5 sm:py-2.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs sm:text-sm"
                />
              </div>
              
              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowMobileFilters(true)}
                className="md:hidden p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover"
              >
                <Filter className="h-4 w-4 text-gray-600" />
              </button>
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <Filter className="h-4 w-4 text-gray-500" />
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-sm"
                >
                  <option value="featured">Featured</option>
                  <option value="newest">Newest</option>
                  <option value="popular">Most Popular</option>
                  <option value="price_low">Price: Low to High</option>
                  <option value="price_high">Price: High to Low</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-primary-50 text-primary-600 shadow-neomorph-inset' 
                      : 'bg-white shadow-neomorph hover:shadow-neomorph-hover'
                  }`}
                >
                  <Grid className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                    viewMode === 'list' 
                      ? 'bg-primary-50 text-primary-600 shadow-neomorph-inset' 
                      : 'bg-white shadow-neomorph hover:shadow-neomorph-hover'
                  }`}
                >
                  <List className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>

            {/* Category Filter - Desktop */}
            {serviceCategories.length > 1 && (
              <div className="hidden md:flex flex-wrap gap-2 pt-2 border-t border-primary-100/50">
                {serviceCategories.map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-[10px] sm:text-sm font-medium transition-all whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-primary-50 text-primary-700 shadow-neomorph-inset border border-primary-200'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    {category === 'all' ? 'All Services' : (
                      <span className="flex items-center gap-1">
                        <span>{getCategoryIcon(category)}</span>
                        {getCategoryName(category)}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category Filter - Mobile (simple row) */}
        {serviceCategories.length > 1 && (
          <div className="md:hidden flex overflow-x-auto hide-scrollbar space-x-2 pb-2 mb-4">
            {serviceCategories.slice(0, 6).map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30'
                    : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
                }`}
              >
                {category === 'all' ? 'All' : (
                  <span className="flex items-center gap-0.5">
                    <span>{getCategoryIcon(category)}</span>
                    <span className="text-[10px]">{getCategoryName(category).slice(0, 8)}</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Services Grid/List - 2 columns on mobile for grid view */}
        {isLoading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3' : 'grid-cols-1'} gap-3 sm:gap-6`}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 animate-pulse">
                <div className="h-28 sm:h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg mb-2 sm:mb-4"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mb-1.5 sm:mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2 mb-2 sm:mb-4"></div>
                <div className="h-6 sm:h-8 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : filteredServices.length > 0 ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3' : 'grid-cols-1'} gap-3 sm:gap-6`}>
            {filteredServices.map(service => (
              <div key={service.id} className={`bg-white rounded-xl shadow-neomorph p-3 sm:p-5 border border-primary-100/30 hover:shadow-neomorph-hover transition-all`}>
                {/* Image - Mobile optimized */}
                {service.images?.[0] ? (
                  <img
                    src={service.images[0]}
                    alt={service.title}
                    className={`w-full object-cover rounded-lg mb-2 sm:mb-4 ${
                      viewMode === 'list' ? 'h-32 sm:h-40 md:h-32' : 'h-28 sm:h-48'
                    }`}
                  />
                ) : (
                  <div className={`w-full bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mb-2 sm:mb-4 ${
                    viewMode === 'list' ? 'h-32 sm:h-40 md:h-32' : 'h-28 sm:h-48'
                  }`}>
                    <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                  </div>
                )}
                
                <div className="space-y-1.5 sm:space-y-3">
                  {/* Title and Featured Badge */}
                  <div className="flex flex-wrap items-start justify-between gap-1">
                    <h3 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2 flex-1">{service.title}</h3>
                    {service.featured && (
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[8px] sm:text-xs font-bold rounded-lg whitespace-nowrap">
                        Featured
                      </span>
                    )}
                  </div>
                  
                  {/* Category with Icon */}
                  {service.category && (
                    <span className="inline-flex items-center text-[8px] sm:text-xs text-gray-600 bg-gray-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                      <span className="mr-0.5 sm:mr-1">{getCategoryIcon(service.category)}</span>
                      {getCategoryName(service.category)}
                    </span>
                  )}
                  
                  {/* Rating */}
                  {service.rating > 0 && (
                    <div className="flex items-center">
                      {renderStars(service.rating)}
                      <span className="ml-0.5 sm:ml-1 text-[9px] sm:text-xs text-gray-500">
                        ({service._count?.reviews || 0})
                      </span>
                    </div>
                  )}
                  
                  {/* Description - Hidden on mobile */}
                  <p className="hidden sm:block text-sm text-gray-600 line-clamp-2">
                    {service.description}
                  </p>
                  
                  {/* Price and Delivery Time */}
                  <div className="flex flex-wrap items-center justify-between gap-1 pt-1">
                    <div>
                      <span className="text-sm sm:text-xl font-bold text-gray-800">
                        {formatAmount(service.basePrice)}
                      </span>
                      {service.priceRange?.max && service.priceRange.max > service.basePrice && (
                        <span className="text-[9px] sm:text-sm text-gray-500 ml-0.5 sm:ml-1">
                          - {formatAmount(service.priceRange.max)}
                        </span>
                      )}
                    </div>
                    {service.deliveryTime && (
                      <div className="flex items-center text-[9px] sm:text-sm text-gray-600 bg-gray-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg">
                        <Clock className="h-2 w-2 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        {service.deliveryTime}
                      </div>
                    )}
                  </div>
                  
                  {/* Completed Orders */}
                  <div className="flex items-center text-[9px] sm:text-sm text-green-600">
                    <CheckCircle className="h-2.5 w-2.5 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                    {service.completedOrders || 0} completed
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1.5 sm:gap-2 pt-1.5 sm:pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleInquire(service)}
                      className="flex-1 px-2 sm:px-3 py-1.5 sm:py-1.5 bg-white text-gray-700 text-[9px] sm:text-sm font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex items-center justify-center"
                    >
                      <MessageSquare className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      Inquire
                    </button>
                    <button
                      onClick={() => handleOrder(service)}
                      className="flex-1 px-2 sm:px-3 py-1.5 sm:py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-[9px] sm:text-sm font-medium rounded-lg shadow-md shadow-primary-500/30 hover:shadow-lg transition-all flex items-center justify-center"
                    >
                      <ShoppingBag className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                      Book
                    </button>
                  </div>

                  {/* Tags - Hidden on mobile */}
                  {service.tags && service.tags.length > 0 && (
                    <div className="hidden sm:flex flex-wrap gap-1 pt-1">
                      {service.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-1.5 py-0.5 bg-gray-50 text-gray-600 text-[10px] rounded-lg border border-gray-200"
                        >
                          {tag}
                        </span>
                      ))}
                      {service.tags.length > 3 && (
                        <span className="px-1.5 py-0.5 text-[10px] text-gray-500 bg-gray-50 rounded-lg">
                          +{service.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-neomorph p-6 sm:p-8 text-center border border-primary-100/30">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-20 sm:h-20 rounded-full bg-primary-50 shadow-neomorph-inset mb-3 sm:mb-4">
              <TrendingUp className="h-7 w-7 sm:h-10 sm:w-10 text-primary-400" />
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No services found</h3>
            <p className="text-xs sm:text-sm text-gray-500">
              {searchQuery || selectedCategory !== 'all' ? 'Try a different search or category' : 'This business has no services listed yet'}
            </p>
            {(searchQuery || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="mt-3 sm:mt-4 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg text-xs sm:text-sm font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mobile Filters Modal */}
      {showMobileFilters && <MobileFiltersModal />}

      {/* Order Modal */}
      {showOrderModal && selectedService && selectedBusiness && (
        <OrderModal
          item={selectedService}
          type="service"
          business={selectedBusiness}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedService(null);
          }}
          onSuccess={(order) => {
            toast.success('Service booked successfully!');
            setShowOrderModal(false);
            setSelectedService(null);
          }}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ServiceListPage;